import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Circle,
  Copy,
  ExternalLink,
  Info,
  KeyRound,
  Loader2,
  Lock,
  Upload,
  XCircle
} from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button, Panel, PanelHeader, PanelSection } from "../../components/primitives";
import { apiUrl, authRequired, googleAllowedDomain, sourceTextLimit } from "../../lib/env";
import { copyToClipboard, formatBytes, formatDate, getMcpEndpoint, maskMcpToken } from "../../lib/format";
import {
  canReadSourceText,
  clearAuthoritativeConcept,
  loadAuthoritativeConcept,
  readableFileType,
  saveAuthoritativeConcept,
  sourceTitleFromFilename,
  sourceTitleFromText
} from "../../lib/storage";
import type { ActionState, AuthoritativeConcept, McpCredential } from "../../lib/types";
import { documentExcerpt, documentPageBlocks, documentTitleFromText } from "./sourceDocument";

/**
 * /settings — the source every run starts from, the backend connection, and AI tool access.
 *
 * THE TRUST BUG THIS SCREEN EXISTED TO HIDE (the /settings analogue of the fabricated 42%):
 * upload a PDF or a DOCX and `canReadSourceText` is false, so `text` is "" and `storageMode`
 * becomes "metadata" — THE TEXT WAS NEVER CAPTURED AND NO RUN CAN USE IT. The old card
 * nevertheless printed the new title in a confident bold heading as the active "Workspace
 * source", and the only disclosure was a transient `sourceMessage`. Navigate away and back and
 * the caution was gone, leaving the UI asserting an active source whose text was never read
 * while every new run silently still started from the packaged canonical.
 *
 * The fix is STRUCTURAL, not a message: [data-testid="source-state"] is DERIVED from
 * `concept.mode` + `concept.storageMode`, so it survives a reload and a navigation, and it says
 * "Text not captured" for as long as that is true. A governance tool may not claim a state
 * stronger than the one it knows — least of all on the single highest-consequence control in
 * the product: which document every future run is personalized FROM.
 *
 * THE OTHER TWO HONESTY FIXES:
 *   - An EXPIRED MCP token used to render identically to a valid one (`credentialReady` never
 *     looked at `expiresAt`). It now reads DANGER / "Expired".
 *   - "Remove local token" read as revocation next to the Copy buttons in identical grey. It
 *     only clears localStorage; the bearer token stays valid until it expires. It is now a
 *     danger button, set apart by a hairline, and it says exactly what it does and does not do.
 *
 * LOCAL COMPONENTS FLAGGED FOR CONSOLIDATION (see notes): TonePill, Disclosure and
 * RuleSentences exist here only because the shared primitives cannot yet express them —
 * StatusPill is typed to RunState|verdict, there is no Disclosure primitive, and RuleList
 * hardcodes a Lock glyph. They compose the primitives' CSS; they do not restyle it.
 */

/* ------------------------------------------------------------------ local primitives */

type Tone = "neutral" | "progress" | "success" | "caution" | "danger";
type Glyph = "dot" | "lock" | "check" | "alert" | "cross" | "spinner";

function ToneGlyph({ glyph }: { glyph: Glyph }) {
  if (glyph === "check") return <CheckCircle2 aria-hidden="true" />;
  if (glyph === "alert") return <AlertTriangle aria-hidden="true" />;
  if (glyph === "cross") return <XCircle aria-hidden="true" />;
  if (glyph === "lock") return <Lock aria-hidden="true" />;
  if (glyph === "spinner") return <Loader2 aria-hidden="true" className="spin" />;
  return <Circle aria-hidden="true" />;
}

/**
 * The shared StatusPill is typed `PillStatus = RunState | ChangeItem["verdict"]`, which is
 * exactly right for a run and cannot describe a source, a backend or a token. This renders the
 * SAME markup and the SAME primitive classes — glyph + text + colour, never colour alone — with
 * the tone stated directly. Consolidation should widen StatusPill to accept this shape.
 */
function TonePill({ tone, glyph, label, status }: { tone: Tone; glyph: Glyph; label: string; status: string }) {
  return (
    <span className={`status-pill tone-${tone}`} data-testid="status-pill" data-status={status} data-tone={tone}>
      <ToneGlyph glyph={glyph} />
      {label}
    </span>
  );
}

/**
 * A thin wrapper over NATIVE <details>/<summary>. [open] is the only styling hook; there is no
 * JS height animation, which would desynchronize from the open attribute the harness reads.
 *
 * IT IS ALWAYS THE FOOT OF A PANEL, NEVER THE PANEL ITSELF. Round 3 shipped "Document rules"
 * as a bare disclosure-as-panel — a wholly recessed grey card — directly beside "Backend
 * connection", a white card with a recessed drawer at its foot. Two panels of EQUAL RANK in
 * the one two-up row on the screen, wearing two different materials: the eye reads that as a
 * mistake before it reads a word of either. Both panels now have the same shell — header,
 * white body, recessed drawer at the foot — and the recessed ground goes on doing its one job
 * (this is reference; you read it, you do not act on it).
 */
function Disclosure({
  summary,
  defaultOpen,
  className,
  children,
  testId
}: {
  summary: string;
  defaultOpen?: boolean;
  className?: string;
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <details className={`settings-disclosure ${className || ""}`} data-testid={testId} open={defaultOpen}>
      <summary>
        <span>{summary}</span>
        <ChevronDown aria-hidden="true" />
      </summary>
      {children}
    </details>
  );
}

/**
 * A LOCK IS NOT A BULLET.
 *
 * The RuleList primitive prints a <Lock> beside every row, which is exactly right on a diff —
 * where a lock means "this fact is sealed and cannot be silently changed" — and wrong here.
 * /settings ran SIX identical amber padlocks down the page as bullets for purely informational
 * rows ("What this changes", "Frontend — Netlify", "Export"). A lock does not mean
 * "informational", and six of them is noise pretending to be hierarchy: when everything is
 * marked as protected, nothing reads as protected.
 *
 * So the static rules on this screen carry themselves TYPOGRAPHICALLY — a semibold label over
 * a secondary sentence, separated by hairlines — and amber survives on this screen only where
 * it means caution (the source notice, the expired-token state). The rows are otherwise
 * IDENTICAL to RuleList's: same class of content, same padding column, same trust rule (a
 * static product rule is a sentence list, never a two-column readout that could be mistaken
 * for a live check). Consolidation should give RuleList an optional `glyph` and delete this.
 *
 * This does NOT restyle the primitive: it does not exist in this markup, so no settings rule
 * can reach it and repaint it on the six other screens that use it.
 */
function NoteList({
  items,
  label,
  className
}: {
  items: Array<{ title: string; body: string }>;
  label: string;
  className?: string;
}) {
  return (
    <ul className={`settings-notes ${className || ""}`} aria-label={label}>
      {items.map((item) => (
        <li key={item.title}>
          <strong>{item.title}</strong>
          <span>{item.body}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * A GREEN CHECK MAY NOT REPORT A DEGRADED OUTCOME.
 *
 * Uploading a PDF used to print a GREEN SUCCESS TICK beside a sentence saying the file's text
 * could NOT be read and that runs would still start from the packaged canonical. The pill and
 * the in-page notice were honest; the glyph beside them was celebrating. Tone is a channel, and
 * a channel that contradicts its own words is worse than no channel — so the degraded path now
 * carries the SAME amber caution tone as the state it is reporting, and green survives only on
 * the path where the text was genuinely captured.
 */
type FeedbackState = Exclude<ActionState, "idle"> | "neutral" | "caution";

function FeedbackLine({
  state,
  message,
  testId
}: {
  state: FeedbackState;
  message: string;
  testId: string;
}) {
  return (
    <p className={`settings-feedback ${state}`} data-testid={testId} data-state={state} role="status">
      {state === "running" ? <Loader2 aria-hidden="true" className="spin" /> : null}
      {state === "success" ? <CheckCircle2 aria-hidden="true" /> : null}
      {state === "error" || state === "caution" ? <AlertTriangle aria-hidden="true" /> : null}
      {state === "neutral" ? <Info aria-hidden="true" /> : null}
      <span>{message}</span>
    </p>
  );
}

/**
 * A CREDENTIAL BREAKS AT ITS SEAM.
 *
 * `maskMcpToken` returns head + "..." + tail, and a masked bearer token has no word boundaries —
 * so at 320 the chip wrapped INSIDE the ellipsis ("atk_live_9f8e7d6c5. / ..abcdef0123"), which
 * reads as a rendering fault on the most sensitive string on the screen. The mask has exactly one
 * seam, and this puts an explicit break opportunity on it: line one ends on the token's own
 * prefix, line two carries the ellipsis and the tail whole.
 *
 * It does not change, shorten or hide one character of what is displayed, and the copy control
 * still yields the full token. A <wbr> is a break OPPORTUNITY, not a character: the accessible
 * name and the text content are byte-identical to the mask.
 */
function MaskedToken({ value }: { value: string }) {
  const seam = value.indexOf("...");
  if (seam < 0) return <code>{value}</code>;
  return (
    <code>
      {value.slice(0, seam)}
      <wbr />
      {value.slice(seam)}
    </code>
  );
}

/* --------------------------------------------------------------------- derived state */

type SourceKind = "canonical" | "text" | "metadata";

/**
 * ONE GEOMETRY, IN EVERY STATE.
 *
 * The control bar used to change SHAPE with the state: canonical printed a sentence beside the
 * buttons, captured printed a different one, and metadata printed NONE — leaving the buttons
 * alone over an empty left half of the row. Three shapes for one component is how a page starts
 * to look assembled rather than designed, and the empty half-row is the dead column in miniature.
 *
 * So every state carries a `consequence`: the ONE sentence that says what this source means for
 * the next run. It is a restatement of the state the pill already carries — no new fact, no new
 * certainty — and in metadata mode it is deliberately SHORTER than the notice on the page, which
 * is where the full explanation (and the remedy) belongs. `sentence` remains the full text used
 * by the page notice.
 */
const SOURCE_STATE: Record<
  SourceKind,
  { tone: Tone; glyph: Glyph; label: string; sentence: string; consequence: string }
> = {
  canonical: {
    tone: "neutral",
    glyph: "lock",
    label: "Packaged canonical",
    sentence: "The ATLAS DPI v2 source packaged with the compiler. Every new run starts from this.",
    consequence: "The ATLAS DPI v2 source packaged with the compiler. Every new run starts from this."
  },
  text: {
    tone: "success",
    glyph: "check",
    label: "Text captured",
    sentence: "New runs start from the text captured from this file.",
    consequence: "New runs start from the text captured from this file."
  },
  metadata: {
    tone: "caution",
    glyph: "alert",
    label: "Text not captured",
    sentence:
      "This browser could not read the text of this file. Its name is recorded, but new runs still start from the packaged canonical source. Upload HTML, Markdown or plain text to capture the text.",
    consequence:
      "This file is recorded as the workspace source, but its text was not captured, so new runs still start from the packaged canonical."
  }
};

type Connection = "connected" | "local" | "checking" | "unreachable";

const CONNECTION_STATE: Record<Connection, { tone: Tone; glyph: Glyph; label: string; sentence: string }> = {
  connected: {
    tone: "success",
    glyph: "check",
    label: "Connected",
    sentence: "Runs are created and QA is executed on the AWS backend."
  },
  local: {
    tone: "neutral",
    glyph: "lock",
    label: "Local preview",
    sentence:
      "No backend is configured in this build. Runs, edits and decisions are recorded in this browser only. Nothing here has been verified by QA."
  },
  checking: {
    tone: "progress",
    glyph: "spinner",
    label: "Checking",
    sentence: "Checking the backend…"
  },
  unreachable: {
    tone: "danger",
    glyph: "cross",
    label: "Not reachable",
    sentence: "The backend did not respond to the last check. Runs are recorded in this browser only."
  }
};

/**
 * WHAT A TOKEN CAN ACTUALLY CALL.
 *
 * The old panel printed ten uppercase chips (HELP, HEALTH, LIST RUNS…) in a bordered
 * chip-farm. This is the same capability, told honestly and read straight off the server:
 * every name and every verb below is transcribed from the `tools/list` response in
 * netlify/functions/mcp.mjs. THERE ARE ELEVEN, NOT TEN — the old screen was already one
 * tool out of date, which is exactly what happens when a security surface is maintained by
 * retyping it. If a tool is added to mcp.mjs, add it here; never guess a name, and never
 * describe a capability the server does not expose.
 */
const MCP_TOOLS: Array<{ name: string; verb: string }> = [
  { name: "atlas.get_help", verb: "Return the docs URL and the recommended workflow." },
  { name: "atlas.health", verb: "Check the endpoint and the backend health endpoint." },
  { name: "atlas.list_runs", verb: "List recent runs from the shared library." },
  { name: "atlas.get_run", verb: "Fetch one run in full, including its proposed changes." },
  { name: "atlas.research_website", verb: "Find an organization's likely official website." },
  { name: "atlas.create_run", verb: "Create a run and its first draft." },
  { name: "atlas.propose_edit", verb: "Save an edit as a proposed change for review." },
  { name: "atlas.review_changes", verb: "Approve, reject or reset proposed changes." },
  { name: "atlas.approve_run", verb: "Run final QA and approve only if the gates pass." },
  { name: "atlas.package_status", verb: "Read package readiness and QA status." },
  { name: "atlas.get_package_link", verb: "Return the package download URL." }
];

/* ------------------------------------------------------------------------- the screen */

export function SettingsPage({
  apiState,
  checkApi,
  mcpCredential,
  mcpCredentialMessage,
  mcpCredentialState,
  refreshMcpCredential,
  removeMcpCredential,
  setToast
}: {
  apiState: "checking" | "connected" | "local";
  checkApi: (options?: { silent?: boolean }) => Promise<"connected" | "local">;
  mcpCredential: McpCredential | null;
  mcpCredentialMessage: string;
  mcpCredentialState: ActionState;
  refreshMcpCredential: () => void;
  removeMcpCredential: () => void;
  setToast: (text: string) => void;
}) {
  const [concept, setConcept] = useState<AuthoritativeConcept>(() => loadAuthoritativeConcept());
  const [sourceState, setSourceState] = useState<ActionState>("idle");
  const [sourceMessage, setSourceMessage] = useState("");
  const [apiCheckState, setApiCheckState] = useState<ActionState>("idle");
  const [lastCheckedAt, setLastCheckedAt] = useState("");
  /* Read ONCE at mount: at >=1024 there is room, so nothing needs hiding. The phone's collapse
     is a space decision, not a content decision, and it is undone where space exists. */
  const [wide] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches
  );
  /* THE SHEET IS SIZED BY THE DOCUMENT, AND THE DOCUMENT IS CLAMPED TO A PAGE.
     The body renders as many of the real paragraphs as the clamp shows, with a measured fade
     and an expander beneath it. The expander appears ONLY when text is genuinely hidden — an
     expander that reveals nothing is a lie the size of a button — so it is measured, never
     assumed, and re-measured once the serif face has actually loaded (a body first measured
     under the fallback face can land a line short when Source Serif 4 swaps in). */
  const [excerptOpen, setExcerptOpen] = useState(false);
  const excerptRef = useRef<HTMLDivElement>(null);
  const [excerptTruncated, setExcerptTruncated] = useState(false);

  /* WHAT THE PAGE PRINTS: the real captured text, as real paragraphs, uncapped. It is derived
     from concept.text on every read, so a source stored by an older build — whose excerpt
     still carries its Markdown heading, or the 190-character "..." that `textExcerpt` is
     capped at — is repaired rather than printed as syntax on the one object that has to look
     like a document. Nothing is invented: when no text was captured this is EMPTY and the page
     renders the honest notice instead. */
  const pageBlocks = documentPageBlocks(concept);

  useLayoutEffect(() => {
    const element = excerptRef.current;
    if (!element || excerptOpen) return;
    setExcerptTruncated(element.scrollHeight > element.clientHeight + 1);
    /* `concept` is the state object the blocks are derived from, so it is the stable dependency
       — `pageBlocks` is a fresh array on every render and would re-run this on every one. */
  }, [concept, excerptOpen]);

  useEffect(() => {
    if (typeof document === "undefined" || !document.fonts?.ready) return;
    document.fonts.ready
      .then(() => {
        const element = excerptRef.current;
        if (!element || excerptOpen) return;
        setExcerptTruncated(element.scrollHeight > element.clientHeight + 1);
      })
      .catch(() => {});
  }, [excerptOpen]);

  async function updateConceptFile(file: File | null) {
    if (!file) return;
    setSourceState("running");
    setSourceMessage(`Reading ${file.name}…`);
    try {
      const canStoreText = canReadSourceText(file) && file.size <= sourceTextLimit;
      const text = canStoreText ? await file.text() : "";
      /* A DOCUMENT NAMES ITSELF. sourceTitleFromText only ever sees HTML (<title>/<h1>/
         og:title), so a Markdown upload fell through to the FILENAME and its own H1 was left
         to be printed — hash and all — in the page body. The heading is now promoted to the
         page's title, which is where a heading belongs, and never rendered as syntax. */
      const inferredTitle = text ? sourceTitleFromText(text) || documentTitleFromText(text) : "";
      const next: AuthoritativeConcept = {
        title: inferredTitle || sourceTitleFromFilename(file.name),
        filename: file.name,
        fileType: readableFileType(file),
        size: file.size,
        updatedAt: new Date().toISOString(),
        mode: "workspace",
        storageMode: text ? "text" : "metadata",
        text,
        /* The stored excerpt is PROSE, not source syntax: it is shown on a rendered page here
           and used as fallback source text by /new and by run.ts. */
        textExcerpt: text ? documentExcerpt(text) : ""
      };
      const saved = saveAuthoritativeConcept(next);
      setConcept(next);
      if (!saved) {
        setSourceState("error");
        setSourceMessage("The source could not be saved in this browser. Try a smaller HTML or Markdown file.");
        return;
      }
      setSourceState("success");
      /* The success line states what actually happened. It does NOT carry the caution on its
         own — [data-testid="source-state"] does, permanently, because this line evaporates. */
      setSourceMessage(
        text
          ? `${file.name} is now the active source. Its text was captured.`
          : `${file.name} is now recorded as the workspace source. Its text could not be read in this browser, so new runs still start from the packaged canonical source.`
      );
    } catch {
      setSourceState("error");
      setSourceMessage("The file could not be read. Try HTML, Markdown or plain text.");
    }
  }

  function resetConcept() {
    clearAuthoritativeConcept();
    setConcept(loadAuthoritativeConcept());
    setSourceState("success");
    setSourceMessage("The packaged ATLAS DPI v2 canonical source is active.");
  }

  async function handleApiCheck() {
    setApiCheckState("running");
    const nextState = await checkApi();
    setApiCheckState(nextState === "connected" ? "success" : "error");
    setLastCheckedAt(new Date().toISOString());
  }

  async function copyValue(value: string, done: string, failed: string) {
    if (!value) return;
    try {
      await copyToClipboard(value);
      setToast(done);
    } catch {
      setToast(failed);
    }
  }

  const sourceBusy = sourceState === "running";
  const credentialBusy = mcpCredentialState === "running";
  const mcpEndpoint = getMcpEndpoint();

  const sourceKind: SourceKind =
    concept.mode === "canonical" ? "canonical" : concept.storageMode === "text" ? "text" : "metadata";
  const source = SOURCE_STATE[sourceKind];
  const isCanonical = concept.mode === "canonical";

  /* apiUrl === "" is a first-class runtime. "Not reachable" would be a lie there: nothing was
     ever configured to reach. */
  const backendConfigured = Boolean(apiUrl);
  const connectionKind: Connection = !backendConfigured
    ? "local"
    : apiCheckState === "running" || apiState === "checking"
      ? "checking"
      : apiState === "connected"
        ? "connected"
        : "unreachable";
  const connection = CONNECTION_STATE[connectionKind];

  const hasToken = Boolean(mcpCredential?.token);
  const tokenExpired = hasToken && Date.parse(mcpCredential?.expiresAt || "") < Date.now();

  return (
    <div className="page-grid settings-grid" data-testid="settings-page">
      <p className="settings-lede">
        The source every run starts from, the backend connection, and AI tool access.
      </p>

      {/* ==================================================== 1. SOURCE DOCUMENT
          The highest-consequence control in the product: which document every future run is
          personalized FROM. It goes first, and its first primary action is above the fold. */}
      <Panel className="settings-source" data-testid="source-panel">
        {/* The state is DERIVED, and it is stated in the header — the same slot, at the same
            size, as the MCP and Backend pills. Three panels presenting state identically in
            one place is what makes the page read as engineered rather than assembled. The
            testid stays on the wrapper, so the honesty fix is untouched: it still derives
            from concept.mode + concept.storageMode and still survives a reload. */}
        <PanelHeader
          title="Source document"
          actions={
            <span className="settings-state-pill" data-testid="source-state" data-state={sourceKind}>
              <TonePill
                tone={source.tone}
                glyph={source.glyph}
                label={source.label}
                status={`source-${sourceKind}`}
              />
            </span>
          }
        />
        <div className="settings-body source-body">
          {/* THE DOCUMENT, AS A DOCUMENT — AND THE PAGE SIZES THE STAGE, NEVER THE OTHER WAY.
              Round 3 stretched the stage to whatever height the column BESIDE it happened to
              reach, so in the CANONICAL state — the state every operator lands on — a short
              page floated dead-centre in a 390px gradient with 150px of empty desk under it.
              That is a marketing hero with a placeholder in it, not a document.

              Two things changed. The stage now HUGS its sheet (no side column to be stretched
              by, because there is no side column: the page is the full-width hero and its
              controls sit at the natural END of the panel, under it). And the packaged baseline
              — which genuinely carries one sentence of prose and no more — is given real PAGE
              FURNITURE rather than prose it does not have: a letterhead, a serif display title,
              its own facts printed on the sheet as a COLOPHON, and the page-stack edge of a
              bound multi-page document. Nothing is synthesized. The page is short because the
              text is short, and it still reads as a page. */}
          <div className="source-stage">
            {/* THE PAGE STACK. The packaged canonical is a BOUND MULTI-PAGE DOCUMENT and an
                uploaded file is one sheet, so the canonical takes --elev-doc-stack (the sheet's
                lift plus the two sheets under it, drawn as hard-edged shadows — no extra DOM,
                no pseudo-element, nothing that can fall out of alignment) and an upload takes
                the single-sheet --elev-doc. The lift is not decoration: it is the one honest
                difference between the two objects, said in a channel that is not words. */}
            <article className="source-page" data-stack={isCanonical ? "true" : "false"}>
              {/* LETTERHEAD, NOT A RAIL. The brand mark over a hairline divider gives the page
                  its identity and its colour. Explicitly NOT `border-top: var(--amber)` — a
                  coloured bar down one edge of a rounded card is banned. */}
              <div className="source-page-letterhead">
                <img src="/meridian-logo.svg" alt="" />
                <span>{isCanonical ? "ATLAS DPI · canonical source" : "Workspace source"}</span>
              </div>

              <h3 className="source-doc-title">{concept.title}</h3>

              {pageBlocks.length ? (
                <>
                  {/* THE DOCUMENT'S OWN PARAGRAPHS. The page used to render `textExcerpt`,
                      which `cleanPreviewText` HARD-CAPS AT 190 CHARACTERS — so a whole concept
                      note showed as one clipped sentence on an otherwise empty sheet, and a
                      preview that is mostly empty page reads as an unfilled placeholder, not as
                      a document. The body is now derived from the real captured text, uncapped,
                      as the author's own paragraphs. Nothing is synthesized: the sheet shows
                      what was captured and no more. */}
                  <div
                    ref={excerptRef}
                    className="source-page-body"
                    data-testid="source-excerpt"
                    data-expanded={excerptOpen ? "true" : "false"}
                    data-truncated={excerptTruncated ? "true" : "false"}
                  >
                    {pageBlocks.map((block, index) => (
                      <p key={`${index}-${block.slice(0, 24)}`}>{block}</p>
                    ))}
                  </div>
                  {excerptTruncated || excerptOpen ? (
                    /* A CONTROL MUST LOOK LIKE A CONTROL. This shipped as an unstyled ghost
                       label — a line of plain prose, on a page made of plain prose, that
                       happened to be clickable — so the fade above it read as text
                       accidentally clipped rather than as a deliberate "there is more". It is
                       a real affordance now: a bordered pill on the page's own margin,
                       carrying a chevron that turns when it opens. */
                    <Button
                      variant="ghost"
                      size="sm"
                      className="source-excerpt-toggle"
                      data-open={excerptOpen ? "true" : "false"}
                      aria-expanded={excerptOpen}
                      /* The visible label is SHORT because `.btn` is `white-space: nowrap` and the
                         page's text column at 320 is 176px: "Show more of the document" is 210px
                         and pushed the sheet 25px past its own stage. The full sentence survives
                         where it costs nothing — in the accessible name. */
                      aria-label={excerptOpen ? "Show less of the document" : "Show more of the document"}
                      onClick={() => setExcerptOpen((open) => !open)}
                    >
                      {excerptOpen ? "Show less" : "Show more"}
                      <ChevronDown aria-hidden="true" />
                    </Button>
                  ) : null}
                </>
              ) : (
                /* THE HONEST EMPTY PAGE. The text was never captured, so there is no body to
                   render — and NOTHING is invented to fill the space. No skeleton lines, no
                   grey bars pretending prose exists. The page is simply, truthfully short, and
                   it carries a real notice on all four sides. */
                <p className="source-page-notice">
                  <AlertTriangle aria-hidden="true" />
                  <span>{SOURCE_STATE.metadata.sentence}</span>
                </p>
              )}

              {/* THE COLOPHON — THE DOCUMENT'S FACTS, PRINTED ON THE DOCUMENT.
                  These four facts were a metadata readout in a column BESIDE the page, which is
                  where a form puts them and not where a document does. A real page carries its
                  own imprint, so they are set on the sheet, under a rule, in the page's own
                  scale: the sheet gains the furniture that makes it read as a printed object,
                  and the panel loses a whole column of chrome. Scannable reference, one label
                  column per fact, still never a dot-joined caption.

                  A row whose value does not exist is NOT RENDERED — the packaged canonical has
                  no byte size, and an empty "Size —" cell is filler pretending to be data. */}
              <footer className="source-colophon">
                <dl>
                  <div>
                    <dt>File</dt>
                    <dd className="mono">{concept.filename}</dd>
                  </div>
                  <div>
                    <dt>Type</dt>
                    <dd>{concept.fileType}</dd>
                  </div>
                  {concept.size ? (
                    <div>
                      <dt>Size</dt>
                      <dd>{formatBytes(concept.size)}</dd>
                    </div>
                  ) : null}
                  <div>
                    <dt>Updated</dt>
                    <dd>{formatDate(concept.updatedAt)}</dd>
                  </div>
                </dl>
              </footer>
            </article>
          </div>

          {/* THE CONTROL BAR — AT THE NATURAL END OF THE PANEL, NEVER BESIDE THE PAGE.
              Two columns inside one panel can only ever be balanced in ONE state: the sheet's
              height is DATA (a captured document is 200px taller than the packaged one) and the
              column beside it is not, so whichever is shorter strands a void — which is exactly
              why three rounds of tuning kept RELOCATING the dead column instead of removing it.
              There is no side column now. What the document MEANS for a run sits on the left of
              one bar, what you can DO about it sits at its end, and nothing can be stranded by
              anything.

              In metadata mode the consequence sentence is ALREADY on the page, inside the amber
              notice, where the operator is actually looking — so it is not printed twice. */}
          <div className="source-control">
            {/* ONE GEOMETRY, IN EVERY STATE: sentence left, actions right, the disabled reason
                on its own full-width line beneath both. The bar used to lose its sentence in
                metadata mode and render two buttons over an empty left half — the dead column
                in miniature — and three different compositions for one component. */}
            <p className="settings-sentence">{source.consequence}</p>

            <div className="source-actions">
              {/* KEEP the class name: ui-breaker whitelists label.source-upload-control in the
                  interactive-element selector used by the tap-target and toast-occlusion
                  audits. The LABEL carries the Button primitive's classes and the <input> is a
                  transparent overlay inside it, so the whole control is one 44px target and
                  the primitive owns its own hover, press and disabled treatment. */}
              <label className={`source-upload-control btn btn-block ${sourceBusy ? "btn-secondary" : "btn-primary"}`}>
                <input
                  type="file"
                  aria-label="Replace source document"
                  data-testid="source-upload"
                  accept=".html,.htm,.pdf,.doc,.docx,.md,.markdown,.txt"
                  disabled={sourceBusy}
                  onChange={(event) => {
                    void updateConceptFile(event.target.files?.[0] || null);
                    event.currentTarget.value = "";
                  }}
                />
                {sourceBusy ? <span className="busy-spinner dark" aria-hidden="true" /> : <Upload aria-hidden="true" />}
                {sourceBusy ? "Reading the file…" : "Replace source document"}
              </label>
              {/* A DISABLED CONTROL IS STILL A CONTROL, AND THIS ONE HAD NOTHING TO CONTROL.
                  In the canonical state — the state every operator lands on — "Restore packaged
                  canonical" was rendered DISABLED: pale grey label on a pale grey fill, sitting
                  beside the screen's one primary. It read as broken rather than as unavailable,
                  and it was the second of the three grey blocks the phone's action zone had
                  become. There is nothing to restore when the packaged canonical is already the
                  source, and the sentence directly beside it says exactly that. So the control
                  appears only when it can DO something: the moment a workspace source is active.
                  No capability is lost — restoring is reachable in every state where restoring
                  means anything — and no dead grey control is shown in the state where it does
                  not. */}
              {isCanonical ? null : (
                <Button variant="secondary" block data-testid="source-reset" disabled={sourceBusy} onClick={resetConcept}>
                  Restore packaged canonical
                </Button>
              )}
            </div>
          </div>

          {sourceState === "idle" ? null : (
            /* A GREEN TICK MAY NOT REPORT A DEGRADED OUTCOME. A successful UPLOAD whose TEXT was
               never captured is not a success — the file is recorded, and every new run still
               starts from the packaged canonical. It reports in the amber of the state it is
               reporting. Green survives on the captured path, which is the only path where
               anything was actually captured. */
            <FeedbackLine
              state={sourceState === "success" && sourceKind === "metadata" ? "caution" : sourceState}
              message={sourceMessage}
              testId="source-feedback"
            />
          )}

          {/* WHAT THE TWO BUTTONS ABOVE ACTUALLY DO — and the band that CLOSES the panel. */}
          <NoteList
            className="settings-notes-2up"
            label="What choosing a source does"
            items={[
              {
                title: "What this changes",
                body: "New runs start from the source selected here. Runs that already exist keep the source they were created with."
              },
              {
                title: "What this never changes",
                body: "Choosing a source does not skip a check. Every export still needs all six QA checks to pass."
              }
            ]}
          />
        </div>
      </Panel>

      {/* ==================================================== 2. AI TOOL ACCESS (MCP)
          The most security-relevant control in the app. The old panel explained the TRANSPORT
          ("HTTP JSON-RPC") and hid the STAKES. A token lets an AI client act in ATLAS AS YOU. */}
      <Panel className="settings-mcp">
        <PanelHeader
          title="AI tool access"
          actions={
            tokenExpired ? (
              <TonePill tone="danger" glyph="cross" label="Expired" status="mcp-expired" />
            ) : hasToken ? (
              <TonePill tone="success" glyph="check" label="Token issued" status="mcp-issued" />
            ) : (
              <TonePill tone="neutral" glyph="dot" label="No token" status="mcp-none" />
            )
          }
        />

        <div className="settings-body">
          {/* THE STAKES, AND THE PAGE'S ONE OTHER PIECE OF COLOUR WITH A JOB.
              This sentence is the reason the panel exists — a token acts in ATLAS AS YOU — and it
              was set as one more line of grey prose in a panel made entirely of grey prose. It is
              a real callout now: a FULL hairline on all four sides (never a rail down one edge),
              the softest accent ground in the palette, and a key glyph. Colour is spent on the one
              sentence in the panel that changes what an operator does next, and nowhere else. */}
          <p className="settings-callout">
            <KeyRound aria-hidden="true" />
            <span>
              An MCP token lets an AI client — Claude Desktop, Cursor, or any MCP-compatible tool — act in ATLAS as
              you: create runs, apply edits, record approvals and package exports. Treat it like a password.
            </span>
          </p>
        </div>

        {/* THE CAPTION WALL. Round 3 said the same thing six times in the same very small grey:
            these two capability rows, the two sentences under the token controls, and the
            sentence under the destructive action were ALL --text-caption --muted, so a security
            panel 1,100px tall had exactly one type size for everything that was not a heading
            and therefore no hierarchy at all. That is the "washed out" complaint in its purest
            form: rank is not a colour you add, it is a difference you make.

            These two rows are the two most consequential sentences in the panel — what a token
            can do, and what it can NEVER do — so they are BODY text, in --body, not a caption.
            The caption tier survives exactly twice in this panel, on the two lines that qualify
            it. */}
        <NoteList
          className="settings-notes-2up"
          label="What a token can and cannot do"
          items={[
            {
              title: "What a token can do",
              body: "Everything you can do in this app: create a run, research, edit, review, approve and package."
            },
            {
              title: "What a token cannot do",
              body: "It cannot skip a QA check. An export driven by a tool still needs all six checks to pass, and every approval it records is attributed to your account."
            }
          ]}
        />

        <PanelSection className="mcp-credential">
          {hasToken ? (
            <>
              {/* The header pill already carries the state at a glance, so the valid case does
                  not repeat it — it states the two facts the pill cannot: whose token, and
                  until when. The EXPIRED case gets its own pill and sentence, here, where the
                  action is: an expired token used to render identically to a valid one. */}
              {tokenExpired ? (
                <div className="settings-state">
                  <TonePill tone="danger" glyph="cross" label="Expired" status="mcp-expired" />
                  <p className="settings-sentence">
                    This token no longer works. Issue a new one to reconnect your tools.
                  </p>
                </div>
              ) : (
                /* Two facts, so they take this screen's ONE reference geometry — the same
                   label/value grid as the source colophon, the connection facts and the build
                   facts — rather than a third dot-joined caption in a panel that already had
                   too many. Four readouts on one screen; one grammar. */
                <dl className="settings-facts">
                  <dt>Issued to</dt>
                  <dd>{mcpCredential?.email}</dd>
                  <dt>Expires</dt>
                  <dd>{formatDate(mcpCredential?.expiresAt || "")}</dd>
                </dl>
              )}

              <p className="settings-label">Token</p>
              {/* A CREDENTIAL IS NOT A WARNING.
                  It was a mono string in AMBER ink on an amber-tinted, amber-hairlined chip —
                  the exact treatment this system reserves for CAUTION and for a sealed protected
                  fact. So the single most sensitive object on the screen was shouting "something
                  is wrong here" in a state where nothing is wrong at all, and it spent the
                  page's only caution colour on a healthy token.
                  A bearer token is a MACHINE STRING, like the endpoint directly below it, so it
                  takes the machine string's surface: the recessed near-black ground, mono in
                  --code-ink, with a lock glyph carrying the "this is secret" meaning in a channel
                  that is not colour. Two machine strings, one language. Amber now appears on this
                  screen only where it means caution: the uncaptured-source notice, and an expired
                  token. */}
              {/* ONE OBJECT, AND IT DOES NOT CLIP. The masked token used to sit in a chip with
                  `white-space: nowrap` and scroll INSIDE it, which at 320 simply cut the string
                  ("…abcdef0123" guillotined, no ellipsis, no affordance) — a credential you
                  cannot read in full is not a credential you can trust. It wraps now, on word
                  boundaries first, and the copy affordance is seated INSIDE the chip exactly as
                  it is in the endpoint below: two machine strings, one object, one language. */}
              <p className="mcp-chip mcp-chip-token" data-testid="mcp-token">
                <Lock aria-hidden="true" />
                <MaskedToken value={maskMcpToken(mcpCredential?.token || "")} />
                <Button
                  variant="ghost"
                  size="sm"
                  className="mcp-chip-copy"
                  aria-label="Copy token"
                  disabled={credentialBusy}
                  onClick={() =>
                    void copyValue(
                      mcpCredential?.token || "",
                      "Token copied to the clipboard.",
                      "The token could not be copied."
                    )
                  }
                >
                  <Copy aria-hidden="true" />
                </Button>
              </p>

              {/* HIERARCHY FOLLOWS CONSEQUENCE, AND THE PAGE HAS ONE ACCENT TO SPEND.
                  "Copy token" was a full-width saturated primary — the loudest object on the
                  entire screen, out-ranking "Replace source document", which is the highest-
                  consequence control in the product. Copying a string you already have is not a
                  primary act. It is demoted; the accent is spent on the one action per panel that
                  earns it. When the token has EXPIRED, reissuing is the only thing that restores
                  the capability, so it — and only then — takes the primary. */}
              {/* ONE RHYTHM PER COLUMN. On the phone these three controls were three different
                  objects: a full-width Copy, a bare centred ghost label for Reissue, and an
                  auto-width left-aligned Remove — three widths and two alignments in one stack,
                  which reads as three unrelated things rather than as the three things you can
                  do with a token. They are one full-width stack on the phone (where the row IS
                  the tap target) and one auto-width row at >=768 (where a 700px grey band is
                  not a button). Reissue is a real secondary button, not a floating word. */}
              <div className="mcp-copy-actions">
                <Button
                  variant={tokenExpired ? "primary" : "secondary"}
                  data-testid="mcp-issue"
                  busy={credentialBusy}
                  disabled={credentialBusy}
                  onClick={refreshMcpCredential}
                >
                  {credentialBusy ? "Waiting for Google…" : "Reissue token"}
                </Button>
              </div>
              {/* ONE caption, attached to the control it governs. It used to be TWO stacked
                  greys — "Reissuing replaces the token stored in this browser." directly on top
                  of "Tokens are issued only on the production app." — which is how a panel ends
                  up with six identical caption lines and no hierarchy. */}
              <p className="settings-caption">
                Reissuing replaces the token stored in this browser.
                {authRequired ? "" : " Tokens are issued only on the production app."}
              </p>

              {/* "Remove local token" read as REVOCATION: it sat next to Copy in identical grey.
                  It is not revocation. It is a danger action, set apart, and it says so. */}
              <div className="mcp-danger">
                <Button
                  variant="danger"
                  disabled={credentialBusy}
                  onClick={() => {
                    removeMcpCredential();
                    setToast("Token removed from this browser.");
                  }}
                >
                  Remove from this browser
                </Button>
                <p className="settings-caption">
                  This removes the token here only. It stays valid until it expires.
                </p>
              </div>
            </>
          ) : (
            <>
              <h3 className="settings-subhead">No token in this browser</h3>
              <p className="settings-lead">
                Sign in with a {googleAllowedDomain} Google account, then issue a token here. The token is stored in
                this browser only.
              </p>
              {/* NEVER disabled in local preview: AuthGate.refreshMcpCredential early-returns
                  with mcpCredentialState="error", and that error IS the feedback. Disabling it
                  would remove the capability and break the harness path. The limitation is a
                  caption, not a disabled state. */}
              <div className="settings-action">
                <Button
                  variant="primary"
                  block
                  data-testid="mcp-issue"
                  busy={credentialBusy}
                  disabled={credentialBusy}
                  onClick={refreshMcpCredential}
                >
                  {credentialBusy ? "Waiting for Google…" : "Issue token"}
                </Button>
              </div>
              {authRequired ? null : (
                <p className="settings-caption">Tokens are issued only on the production app.</p>
              )}
            </>
          )}

          {/* NARROWED to credential issue/error. It used to share one <p> with copyMessage, and
              copyMessage won — so "Endpoint copied." could mask a real credential error. Copy
              confirmations now go to the ONE app toast. */}
          {mcpCredentialState === "idle" || !mcpCredentialMessage ? null : (
            <FeedbackLine state={mcpCredentialState} message={mcpCredentialMessage} testId="mcp-feedback" />
          )}
        </PanelSection>

        {/* THE TECHNICAL DRAWER. The panel below the credential is REFERENCE — an endpoint, a
            transport, an authentication scheme, a list of method names — and it was rendered in
            the same flat white as the action zone above it, which is what made this panel an
            850px undifferentiated wall. Reference recesses: it sits on the tinted ground, one
            tonal step back from the surface an operator acts on. Same idea as the dark endpoint
            chip, at the scale of the whole block. */}
        <Disclosure summary="Connection details" className="settings-drawer" defaultOpen={wide}>
          <div className="settings-body">
            {/* THE ENDPOINT AND WHAT IT SPEAKS, SIDE BY SIDE.
                The chip used to stop at ~60% of a 1072px column with 400px of dead white beside
                it, while every other block in the panel — the two-up notes, the tool table — ran
                the full width. An arbitrary half-width block in a full-width column reads as
                unfinished. The two facts that describe the endpoint are exactly what belongs in
                that space, so the row is one object: the address on the left, how you talk to it
                on the right. Nothing floats and nothing is stranded. */}
            <div className="mcp-connection">
              <div className="mcp-endpoint-block">
                <p className="settings-label">Endpoint</p>
                {/* ONE OBJECT. This was a full-bleed near-black slab with a ghost "Copy endpoint"
                    button floating underneath it, inset 16px from every other left edge in the
                    panel because `.btn` carries its own horizontal padding. The URL and the act
                    of copying it are the same concern, so they are the same control: the copy
                    button is seated INSIDE the chip. And the URL WRAPS rather than scrolling
                    inside a nowrap box — at 320 that box simply clipped the address. */}
                <p className="mcp-chip">
                  <code>{mcpEndpoint}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mcp-chip-copy"
                    aria-label="Copy endpoint"
                    onClick={() =>
                      void copyValue(
                        mcpEndpoint,
                        "Endpoint copied to the clipboard.",
                        "The endpoint could not be copied."
                      )
                    }
                  >
                    <Copy aria-hidden="true" />
                  </Button>
                </p>
              </div>

              <dl className="settings-facts mcp-transport">
                <dt>Transport</dt>
                <dd>HTTP JSON-RPC</dd>
                <dt>Authentication</dt>
                <dd>Bearer token</dd>
              </dl>
            </div>

            {/* THE CAPABILITY THE REBUILD DROPPED. A token acts in ATLAS as you, so the
                operator issuing one is entitled to see EXACTLY what it can call. Read straight
                off the server's tools/list — sentence case, no uppercase, no bordered chips.

                It was a bare four-column text dump, and — because nothing in base.css resets a
                <ul> — it also sat on the browser's default 40px list indent, so its left edge
                missed the panel's one content column by about 30px while every other block in
                the panel landed on it. It is now a DIVIDED ROW RHYTHM: mono method name, plain-
                language verb, hairline between rows, on the panel's content column exactly. */}
            <div className="mcp-tools">
              <p className="settings-label">Tools this token can call</p>
              <ul className="mcp-tool-grid" aria-label="Tools this token can call">
                {MCP_TOOLS.map((tool) => (
                  <li key={tool.name}>
                    <code>{tool.name}</code>
                    <small>{tool.verb}</small>
                  </li>
                ))}
              </ul>
            </div>

            {/* DEMOTED from a full-width blue primary. The loudest button on a security panel
                must not be a link to documentation. */}
            <div className="mcp-doc-links">
              <a className="btn btn-ghost" href="/help/mcp.html" target="_blank" rel="noreferrer">
                <ExternalLink aria-hidden="true" />
                MCP setup guide
              </a>
              <a className="btn btn-ghost" href="/help/mcp.md" target="_blank" rel="noreferrer">
                Markdown version
              </a>
            </div>
          </div>
        </Disclosure>
      </Panel>

      {/* ================================================ 3 + 4. THE CLOSING BAND
          A PLAIN LAYOUT DIV, not a Panel — the panel-in-panel ban is untouched.

          THE DEAD COLUMN, AND WHY IT KEPT COMING BACK. The first rebuild pinned four panels
          into an explicit 2x2 grid and left a ~190px void under Source document. Round 1
          unpinned that, spanned the hero, and merely MOVED the void: the MCP panel ran 310px
          past the rail beside it.

          Both of those columns are sized by DATA, not by layout — the source panel grows with
          the captured excerpt, and this panel is ~150px taller once a token exists — so no
          pairing that includes either of them can be balanced in every state.

          So the two panels below are the only two on this screen whose content is STATIC, and
          they are the only two put side by side (at >=1024). Everything that varies spans the
          full width, where it cannot strand anything. */}
      <div className="settings-utility">
        {/* -------------------------------------------------- 3. BACKEND CONNECTION */}
        <Panel className="settings-backend">
          <PanelHeader
            title="Backend connection"
            actions={
              <span className="settings-state-pill" data-testid="connection-state" data-state={connectionKind}>
                <TonePill
                  tone={connection.tone}
                  glyph={connection.glyph}
                  label={connection.label}
                  status={`connection-${connectionKind}`}
                />
              </span>
            }
          />
          <div className="settings-body">
            {/* The sentence gets the FULL body width now that the pill is in the header. It
                used to sit beside the pill inside a 320px rail and wrap to about eighteen
                characters a line — a ragged narrow column that just looked broken. */}
            <p className="settings-sentence">{connection.sentence}</p>

            {/* A governance tool does not assert a connection it has not verified. */}
            <p className="settings-caption">
              {lastCheckedAt ? `Last checked ${formatDate(lastCheckedAt)}` : "Not checked yet"}
            </p>

            {/* A BUTTON IS SIZED TO ITS JOB, NOT TO ITS PANEL. Full-width is a PHONE decision
                — a thumb aims at the whole row — and this panel is 728px wide at 1440. The
                wrapper is a grid that shrink-wraps to the control at >=768, so the button
                stops being a 680px grey band and the primitive's own geometry is untouched. */}
            <div className="settings-action">
              <Button
                variant="secondary"
                block
                data-testid="api-check"
                busy={apiCheckState === "running"}
                disabled={apiCheckState === "running"}
                onClick={() => void handleApiCheck()}
              >
                {apiCheckState === "running" ? "Checking…" : "Check backend"}
              </Button>
            </div>

            {/* When no backend is CONFIGURED, `fetch("/health")` still 200s against the dev
                server's SPA fallback, so a naive "The backend responded." would be the
                /settings analogue of the fabricated 42%: a check reporting a pass it never
                really ran. The note defers to the persistent state — it never claims a backend
                responded when none was ever configured to respond. */}
            {apiCheckState === "idle" ? null : (
              <FeedbackLine
                state={backendConfigured ? (apiCheckState as FeedbackState) : "neutral"}
                testId="api-check-note"
                message={
                  !backendConfigured
                    ? "No backend is configured in this build, so there is nothing to reach."
                    : apiCheckState === "running"
                      ? "Checking the AWS backend…"
                      : apiCheckState === "success"
                        ? "The backend responded."
                        : "The backend did not respond."
                }
              />
            )}
          </div>

          {/* WHAT A CHECK ACTUALLY PROVES — AND WHAT IT CANNOT.
              This is the third instance of the screen's ONE closing grammar (the source panel and
              the MCP panel both end on the same pair: what this changes / what this never
              changes), and it is here because the sentence it carries is the most misreadable
              thing on the panel: an operator who sees "Connected" can very easily hear "verified".
              It is not filler — it is the honesty rule of this panel, said in words — and it is
              also what makes Backend carry comparable content to the panel beside it, so the two
              cards can END TOGETHER without a single pixel of manufactured whitespace. Balance the
              content, then the boxes. Never the other way round. */}
          <NoteList
            className="settings-notes-2up"
            label="What checking the backend does"
            items={[
              {
                title: "What a check does",
                body: "It asks the backend whether it can be reached from this browser, right now. That is all it establishes."
              },
              {
                title: "What a check never does",
                body: "A reachable backend is not a passed QA gate. Every export still needs all six QA checks to pass."
              }
            ]}
          />

          {/* STATIC BUILD FACTS. They were in a DataList, which the contract reserves for LIVE
              STATE ONLY — "if a value cannot change, it MAY NOT go in a DataList" — so a static
              product fact could be read as a verified check. They are reference, so they take
              this screen's ONE reference geometry: the same aligned label/value grid as the
              source facts and the connection facts, in the same recessed drawer. Three readouts
              on one screen, one grammar. */}
          <Disclosure summary="Build details" className="settings-drawer" defaultOpen={wide}>
            <div className="settings-body">
              <dl className="settings-facts">
                {/* THE FACT THE PANEL WAS MISSING, AND IT IS THE ONE THE PANEL IS ABOUT: WHERE.
                    "Backend connection" listed the hosting and the models and never once said
                    what address it would call — so the panel was both thin (a 138px void under
                    its last row) and incomplete. This is read straight from the build's own
                    `apiUrl`, and when there is none it says so in plain words rather than
                    printing an empty cell or implying an address exists. */}
                <dt>API base</dt>
                <dd className={apiUrl ? "mono" : ""}>{apiUrl || "None configured in this build"}</dd>
                <dt>Frontend</dt>
                <dd>Netlify</dd>
                <dt>Backend</dt>
                <dd>AWS Lambda</dd>
                <dt>Models</dt>
                <dd>Gemini for content, Claude for design</dd>
              </dl>
            </div>
          </Disclosure>
        </Panel>

        {/* -------------------------------------------------- 4. DOCUMENT RULES
            Reference. Safe to collapse on phone: no undecided work is hidden behind it.

            SAME RANK, SAME SHELL. This shipped as a bare disclosure-as-panel — a wholly
            RECESSED grey card — sitting beside Backend connection, a WHITE card with a recessed
            drawer at its foot. Two panels of equal rank, side by side in the screen's one two-up
            row, made of two different materials: the eye reads a mistake before it reads a word.
            It now has the identical shell — header, white body carrying the one sentence that
            says what these rules ARE, recessed drawer at the foot carrying the reference itself
            — so the row is one component twice, and the recessed ground still means exactly what
            it means everywhere else on this screen: you read here, you do not act here. */}
        <Panel className="settings-rules">
          <PanelHeader title="Document rules" />
          <div className="settings-body">
            <p className="settings-sentence">
              Every run is compiled under these four rules. Nothing on this screen changes them.
            </p>
          </div>
          <Disclosure summary="The four rules" className="settings-drawer" defaultOpen={wide}>
            <NoteList
              className="settings-notes-2up"
              label="Document rules"
              items={[
              {
                title: "Protected facts",
                body: "Numbers, dates, currencies, citations, URLs and defined terms are never changed silently. Every one is marked in the before/after diff."
              },
              {
                title: "Substantive edits",
                body: "Any change to the argument is flagged and needs its own approval. It is never applied for you."
              },
              {
                title: "Layout",
                body: "Rendering follows the Meridian print style. When a replacement is too long, the text is shortened. Fonts, spacing and layout are never adjusted to make it fit."
              },
              {
                title: "Export",
                body: "A package is released only after all six QA checks return a pass. Nothing on this screen changes that."
              }
              ]}
            />
          </Disclosure>
        </Panel>
      </div>
    </div>
  );
}
